import React, { useContext, useEffect, useRef } from 'react';
import styled from 'astroturf/react';
import { useToast } from 'js/design_system/toasts/hooks';
import { CiterightContext } from '../CiterightContextProvider';
import type { ClaimWithActions } from '../types';

const ToastContent = styled.span`
  display: flex;
  align-items: center;
  gap: var(--spacing-xs-12);
  padding: var(--spacing-xs-8) var(--spacing-sm-24);
  background: var(--primary-brand);
  color: var(--text-white);
  border-radius: var(--border-radius-radius-sm);
  border: 1px solid var(--primary-brand);
  min-width: 300px;
`;

const ToastMessage = styled.span`
  flex: 1;
`;

const ToastLink = styled.button`
  background: none;
  border: none;
  color: var(--text-white);
  cursor: pointer;
  text-decoration: underline;
  font-weight: 600;
  padding: 0;

  &:hover {
    opacity: 0.8;
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: var(--text-white);
  cursor: pointer;
  font-size: var(--type-size-sm-5);
  padding: var(--spacing-xs-4) var(--spacing-xs-8);
  line-height: 1;

  &:hover {
    opacity: 0.8;
  }
`;

/**
 * Custom hook that shows a toast notification when a claim receives its first suggested work.
 * The toast contains a link to scroll to the claim and can be dismissed.
 * Only one toast is shown at a time - new toasts automatically replace existing ones.
 *
 * @param claimRefs - Map of claim IDs to their React refs for scrolling
 * @param scrollToAndExpandClaim - Function to scroll to and expand a specific claim
 */
export const useFirstSuggestedWorkToast = (
  claimRefs: Map<string, React.RefObject<HTMLSpanElement>>,
  scrollToAndExpandClaim: (claimId: string) => void,
): void => {
  const context = useContext(CiterightContext);
  const { toast, closeAll } = useToast();
  const shownToastsForClaims = useRef<Set<string>>(new Set());

  useEffect(() => {
    const report = context?.report;
    if (!report) return;

    // Check each claim for first suggested work
    report.claims.forEach((claim: ClaimWithActions) => {
      // Skip if we've already shown a toast for this claim
      if (shownToastsForClaims.current.has(claim.id)) return;

      // Check if claim has suggested works
      const hasSuggestedWorks =
        claim.search?.suggested_works &&
        claim.search.suggested_works.length > 0;

      if (hasSuggestedWorks) {
        // Mark this claim as having shown a toast
        shownToastsForClaims.current.add(claim.id);

        // Close all existing toasts before showing the new one
        closeAll();

        // Create toast content with link to scroll to claim
        const FirstWorkToast = (_id: string, closeToast?: () => void) => {
          const handleScrollToClaim = () => {
            scrollToAndExpandClaim(claim.id);
            closeToast?.();
          };

          const handleCloseToast = () => {
            closeToast?.();
          };

          const suggestedWorksCount = claim.search?.suggested_works?.length;
          let toastMessage = `Found references for your claim "${claim.text}".`;
          if (suggestedWorksCount === 1) {
            toastMessage = `Found ${suggestedWorksCount} reference for your claim "${claim.text.slice(0, 50)}...".`;
          } else if (suggestedWorksCount !== undefined) {
            toastMessage = `Found ${suggestedWorksCount} references for your claim "${claim.text.slice(0, 50)}...".`;
          }

          return (
            <ToastContent>
              <ToastMessage>
                {toastMessage}{' '}
                <ToastLink onClick={handleScrollToClaim}>View claim</ToastLink>
              </ToastMessage>
              <CloseButton onClick={handleCloseToast} aria-label="Close">
                ×
              </CloseButton>
            </ToastContent>
          );
        };

        toast({
          text: 'Found references for a claim.',
          render: FirstWorkToast,
          placement: 'bottom-end',
          autoClose: true,
          delay: 10000,
          closeable: true,
        });
      }
    });
  }, [context?.report, toast, scrollToAndExpandClaim, closeAll]);
};
